import { 
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from './config';
import { getSiteMedia } from './siteMedia';
import { calculateTotalReputation } from './userReputation'; // Import new reputation function

// Define user roles and their capabilities
export const USER_ROLES = {
  RESEARCHER: 'researcher',
  CONTRIBUTOR: 'contributor',
  EXPERT: 'expert',
  ADMIN: 'admin'
};

// Define role capabilities including site posting limits
export const ROLE_CAPABILITIES = {
  [USER_ROLES.RESEARCHER]: {
    label: 'Researcher',
    description: 'Basic access with limited contribution capabilities',
    sitePostingLimit: 5,
    mediaPerSiteLimit: 10,
    canCreateSites: true,
    canCommentOnSites: true,
    canCreateSublocations: false,
  },
  [USER_ROLES.CONTRIBUTOR]: {
    label: 'Contributor',
    description: 'Regular contributor with standard posting limits',
    sitePostingLimit: 15,
    mediaPerSiteLimit: 25,
    canCreateSites: true,
    canCommentOnSites: true,
    canCreateSublocations: true,
  },
  [USER_ROLES.EXPERT]: {
    label: 'Expert',
    description: 'Recognized expert with higher posting limits',
    sitePostingLimit: 50,
    mediaPerSiteLimit: 100,
    canCreateSites: true,
    canCommentOnSites: true,
    canCreateSublocations: true,
    canFeatureContent: true,
  },
  [USER_ROLES.ADMIN]: {
    label: 'Administrator',
    description: 'Full system access with no limits',
    sitePostingLimit: Infinity,
    mediaPerSiteLimit: Infinity,
    canCreateSites: true,
    canCommentOnSites: true,
    canCreateSublocations: true,
    canFeatureContent: true,
    canModerate: true,
    canManageUsers: true,
  }
};

// Get user profile with role capabilities
export const getUserProfile = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      return { 
        success: false, 
        error: 'User not found' 
      };
    }
    
    const userData = userSnap.data();
    const role = userData.role || USER_ROLES.RESEARCHER;
    const capabilities = ROLE_CAPABILITIES[role] || ROLE_CAPABILITIES[USER_ROLES.RESEARCHER];
    
    return {
      success: true,
      data: {
        ...userData,
        role,
        capabilities
      }
    };
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return { 
      success: false, 
      error: 'Failed to fetch user profile' 
    };
  }
};

// Update user profile
export const updateUserProfile = async (userId, updates) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, updates);
    
    return { success: true };
  } catch (error) {
    console.error('Error updating user profile:', error);
    return { 
      success: false, 
      error: 'Failed to update user profile' 
    };
  }
};

// Helper function to fetch media for a site
const fetchSiteMedia = async (siteId) => {
  try {
    const mediaResult = await getSiteMedia(siteId);
    return mediaResult.success ? mediaResult.data : [];
  } catch (error) {
    console.error('Error fetching site media:', error);
    return [];
  }
};

export const getUserReputation = async (userId) => {
  // Use the new reputation system
  return calculateTotalReputation(userId);
};

export const getUserSites = async (userId) => {
  try {
    const sitesRef = collection(db, 'sites');
    const q = query(
      sitesRef,
      where('submittedBy', '==', userId)
    );
    
    const sitesSnap = await getDocs(q);
    const sitesPromises = sitesSnap.docs.map(async (doc) => {
      const siteData = {
        id: doc.id,
        ...doc.data()
      };
      
      // Fetch media from site_media collection
      const media = await fetchSiteMedia(doc.id);
      siteData.media = media;
      
      return siteData;
    });
    
    const sites = await Promise.all(sitesPromises);

    return {
      success: true,
      data: sites
    };
  } catch (error) {
    console.error('Error fetching user sites:', error);
    return { success: false, error: 'Failed to fetch user sites' };
  }
};
